import {
  ToolbarButtonIcon,
  ToolbarDropdownTriggerButton,
} from 'module/ui/components/atoms/rich-text-editor/toolbar/components';
import {
  faArrowDownBigSmall,
  faArrowUpBigSmall,
  faPenNib,
} from '@fortawesome/pro-regular-svg-icons';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { Editor } from '@tiptap/core';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { snakeCase } from 'lodash';
import { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Dropdown } from '../../../../module/ui/components/atoms/dropdown/dropdown';
import { ToolBarItemProps } from '../../../../module/ui/components/atoms/rich-text-editor/toolbar/types';
import { AskAiCommand } from '../extensions/ask-ai/commands/types';

export interface AskAiToolbarConfig {
  showEnhanceWithMyNotes?: boolean;
}

export const askAiCommandDetailMap = {
  [AskAiCommand.EnhanceWithMyNotes]: {
    icon: (
      <ToolbarButtonIcon
        icon={faPenNib}
        style={{ color: 'var(--purple-60)' }}
      />
    ),
    execute: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().enhanceWithMyNotes().run();
    },
  },
  [AskAiCommand.MakeLonger]: {
    icon: <ToolbarButtonIcon icon={faArrowUpBigSmall} />,
    execute: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().makeLonger().run();
    },
  },
  [AskAiCommand.MakeShorter]: {
    icon: <ToolbarButtonIcon icon={faArrowDownBigSmall} />,
    execute: ({ editor }: { editor: Editor }) => {
      editor.chain().focus().makeShorter().run();
    },
  },
};

const commandAsMenuItem = (command: AskAiCommand) => {
  return {
    key: command,
    label: (
      <FormattedMessage id={`editor.toolbar.ask_ai.${snakeCase(command)}`} />
    ),
    icon: askAiCommandDetailMap[command].icon,
  };
};

export const getAskAiCommandsMenu = (config?: AskAiToolbarConfig) => {
  const commands: any[] = [];

  // Conditionally add EnhanceWithMyNotes
  if (config?.showEnhanceWithMyNotes) {
    commands.push(commandAsMenuItem(AskAiCommand.EnhanceWithMyNotes));
    commands.push({ type: 'divider' });
  }

  // Always add these commands
  commands.push(commandAsMenuItem(AskAiCommand.MakeLonger));
  commands.push(commandAsMenuItem(AskAiCommand.MakeShorter));

  return [
    {
      type: 'group',
      key: 'ask-ai',
      label: <FormattedMessage id="editor.toolbar.ask_ai.group_header" />,
      children: commands,
    },
  ];
};

export const askAiCommandsMenu = getAskAiCommandsMenu();

export const AskAiCommandsDropdown: FC<ToolBarItemProps> = ({
  editor,
  afterCommandExecuted,
}) => {
  const dropdownMenuProps = useMemo(() => {
    return {
      onMouseDown: (e) => {
        e.preventDefault();
        e.stopPropagation();
      },
      onClick: ({ key, domEvent }) => {
        domEvent.preventDefault();
        domEvent.stopPropagation();
        askAiCommandDetailMap[key].execute({ editor });
        afterCommandExecuted?.();
      },
      items: askAiCommandsMenu as ItemType[],
    };
  }, [afterCommandExecuted, editor]);

  return (
    <Dropdown menu={dropdownMenuProps}>
      <ToolbarDropdownTriggerButton
        icon={<ToolbarButtonIcon icon={faSparkles} />}
      >
        <FormattedMessage id="editor.toolbar.ask_ai.label" />
      </ToolbarDropdownTriggerButton>
    </Dropdown>
  );
};
